import { ReactNode } from "react";

import { ConfirmationPageContext } from "../../hooks/use-confirmation-page";
import {
    Metadata,
    RegisteredMetadata
} from "../../hooks/use-registered-metadata";

import { ScreenList } from "./jira-screen-dictionary";

export const TEAM_CHOICE = "teamChoice" as const;
export const TEMPLATE_CHOICE = "templateChoice" as const;
export const PROVISIONING = "provisioning" as const;
export const PROVISIONING_WITH_WIZARD = "provisioningWithWizard" as const;
export const JTBD = "jobsToBeDone" as const;
export const PROJECT_LANDING_SELECTOR = "projectLandingSelector" as const;

export type ProvisioningScreenKey =
    | typeof TEAM_CHOICE
    | typeof TEMPLATE_CHOICE
    | typeof JTBD
    | typeof PROVISIONING
    | typeof PROVISIONING_WITH_WIZARD
    | typeof PROJECT_LANDING_SELECTOR;

export type Conditional<T> = {
    default: T;
    conditionalOR?: Condition<T>[];
};
export type Condition<T> = {
    conditionalAND: ConditionPair<T>[];
};
export type ConditionPair<T> = {
    key?: string;
    value?: T;
    conditionalOR?: Condition<T>[];
};

export type Config = {
    onMount: (confirmationPageContext: ConfirmationPageContext) => void;
    position: Conditional<number>;
    visibility?: Conditional<Metadata>;
};
export type ScreenConfig = Partial<Record<ProvisioningScreenKey, Config>>;
export type ProvisioningScreenDictionary = Partial<
    Record<ProvisioningScreenKey, ReactNode>
>;

export type ScreenListProps<T> = {
    metadata: RegisteredMetadata;
    screensConfig: ScreenConfig;
    fullScreenList: T;
};

export function getConfiguredScreenList({
    metadata,
    screensConfig,
    fullScreenList
}: ScreenListProps<ScreenList>): ScreenList {
    return (
        fullScreenList
            // filter on screens that exists in config.
            .filter((screen) => screensConfig[screen])
            // get screens that should be shown to this user.
            .filter((screen) =>
                getVisibility(metadata, screensConfig[screen]?.visibility)
            )
            // sort screens based on the explicit position.
            .sort((screenA, screenB) => {
                const configA =
                    screensConfig[screenA] ??
                    ({ position: { default: 150 } } as Config);
                const configB =
                    screensConfig[screenB] ??
                    ({ position: { default: 150 } } as Config);

                return configA.position.default - configB.position.default;
            })
    );
}

function getVisibility(
    metadata: RegisteredMetadata,
    visibility: Conditional<Metadata> | undefined
) {
    // if no visbility conditions are set, show the screen by default
    if (visibility == null) {
        return true;
    }

    // if visibility is set to true by default, show the screen
    if (visibility?.default) {
        return true;
    }

    const { conditionalOR = [] } = visibility;
    return evaluateConditions(metadata, conditionalOR);
}

function evaluateConditions(
    metadata: RegisteredMetadata,
    conditions: Condition<Metadata>[]
): boolean {
    if (!conditions) {
        return true;
    }

    const reducedOrResults = conditions.map((OR) =>
        OR.conditionalAND
            .map(({ key, value, conditionalOR }) => {
                const metadataValue = metadata?.get(key ?? "");

                // if key is undefined, set pass to true. ConditionalOr will be evaluated instead.
                let pass = key == undefined;

                // check that each value is in metadata
                if (Array.isArray(value) && Array.isArray(metadataValue)) {
                    pass =
                        value.filter(
                            (val = false) =>
                                metadataValue.filter((meta) => meta == val)
                                    .length > 0
                        ).length === value.length;
                } else {
                    // evaluate condition against stored metadata
                    pass = metadataValue == value;
                }

                if (conditionalOR) {
                    pass = pass && evaluateConditions(metadata, conditionalOR);
                }

                return pass;
            })
            .reduce(
                // consolidate conditions in AND clause to one boolean value.
                (conditionOne, conditionTwo) => conditionOne && conditionTwo
            )
    );

    // at least one OR condition should be true to show the screen
    return reducedOrResults.filter((res) => res === true).length > 0;
}
